import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { CANCEL_REASON_CODES, type CancelReasonCode } from '@/types/inspection-lead'

/**
 * Модальное окно отмены лида: обязательная причина из словаря
 * (inspection_leads.cancel_reason — для аналитики воронки) и необязательный
 * комментарий (cancel_comment). Одна механика для всех воронок.
 */
export function CancelLeadDialog({
  open,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean
  pending: boolean
  onConfirm: (reason: CancelReasonCode, comment: string | null) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState<CancelReasonCode | ''>('')
  const [comment, setComment] = useState('')

  if (!open) return null

  const options = CANCEL_REASON_CODES.map((code) => ({
    value: code,
    label: t(`leads.cancelReason.${code}`),
  }))

  const handleClose = () => {
    setReason('')
    setComment('')
    onClose()
  }

  return (
    <Modal
      title={t('leads.cancel.title')}
      onClose={handleClose}
      footer={
        <div className="flex justify-end gap-[var(--stryzh-space-3)]">
          <Button variant="ghost" onClick={handleClose} disabled={pending}>
            {t('common.back')}
          </Button>
          <Button
            variant="danger"
            disabled={!reason || pending}
            onClick={() => {
              if (reason) onConfirm(reason, comment.trim() || null)
            }}
          >
            {pending ? t('leads.cancel.pending') : t('leads.cancel.confirm')}
          </Button>
        </div>
      }
    >
      <p className="mb-[var(--stryzh-space-3)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
        {t('leads.cancel.body')}
      </p>
      <Select<CancelReasonCode>
        value={reason}
        onChange={setReason}
        options={options}
        emptyLabel={t('leads.cancel.placeholder')}
      />
      <textarea
        className="stryzh-input-field mt-[var(--stryzh-space-3)] min-h-[72px] w-full resize-y py-2 leading-snug"
        value={comment}
        placeholder={t('leads.cancel.commentPlaceholder')}
        onChange={(e) => setComment(e.target.value)}
      />
    </Modal>
  )
}
